import type { ReactElement, ReactNode } from 'react';

/**
 * Ícones.
 *
 * Eram glifos Unicode — ⚇, ⚯, ⏏, ♪ — e um glifo não é um ícone: quem o
 * desenha é a fonte do sistema, logo o mesmo botão saía diferente em cada
 * máquina (e, onde a fonte não tivesse o caracter, saía o rectângulo vazio).
 * Desenhados aqui são sempre os mesmos, herdam a cor e o tamanho de quem os
 * usa, e não custam um pedido de rede nem uma dependência.
 *
 * Traço de 1.8 em grelha de 24 com pontas redondas — o peso que acompanha o
 * texto da interface sem o abafar.
 */
const PATHS = {
  hash: (
    <>
      <path d="M4 9h16" />
      <path d="M4 15h16" />
      <path d="M10 3 8 21" />
      <path d="M16 3l-2 18" />
    </>
  ),

  /** Categoria de canais. */
  folder: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h3.5l2 2.5H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
    </>
  ),

  /** Seta de abrir e fechar uma categoria. */
  chevron: (
    <>
      <path d="m9 6 6 6-6 6" />
    </>
  ),

  /** Canal de voz, e quem está em voz. */
  volume: (
    <>
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19 5a10 10 0 0 1 0 14" />
    </>
  ),

  /** Silenciado só para quem está a ver. */
  volumeX: (
    <>
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="m22 9-5 6" />
      <path d="m17 9 5 6" />
    </>
  ),

  mic: (
    <>
      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
      <path d="M19 11v1a7 7 0 0 1-14 0v-1" />
      <path d="M12 19v3" />
      <path d="M9 22h6" />
    </>
  ),

  micOff: (
    <>
      <path d="M9 9v3a3 3 0 0 0 5.1 2.1" />
      <path d="M15 9.3V5a3 3 0 0 0-5.9-.7" />
      <path d="M17 16.9A7 7 0 0 1 5 12v-1" />
      <path d="M19 11v1a7 7 0 0 1-.2 1.4" />
      <path d="M12 19v3" />
      <path d="M9 22h6" />
      <path d="m3 3 18 18" />
    </>
  ),

  headphones: (
    <>
      <path d="M4 17v-5a8 8 0 0 1 16 0v5" />
      <path d="M4 15h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1v-5Z" />
      <path d="M20 15h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-5Z" />
    </>
  ),

  headphonesOff: (
    <>
      <path d="M4 17v-5a8 8 0 0 1 16 0v5" />
      <path d="M4 15h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1v-5Z" />
      <path d="M20 15h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-5Z" />
      <path d="m3 3 18 18" />
    </>
  ),

  screenShare: (
    <>
      <path d="M2 5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5Z" />
      <path d="M12 16v4" />
      <path d="M9 20h6" />
      <path d="M12 12V7" />
      <path d="m9.5 9.5 2.5-2.5 2.5 2.5" />
    </>
  ),

  /** Câmara. */
  video: (
    <>
      <rect x="2" y="6" width="13" height="12" rx="2" />
      <path d="m15 11 6-4v10l-6-4Z" />
    </>
  ),

  /**
   * Ajustes. Barras com punhos e não uma roda dentada: a 17px os dentes da
   * roda viram uma mancha, e isto lê-se.
   */
  settings: (
    <>
      <path d="M4 8h9" />
      <path d="M17 8h3" />
      <circle cx="15" cy="8" r="2" />
      <path d="M4 16h3" />
      <path d="M11 16h9" />
      <circle cx="9" cy="16" r="2" />
    </>
  ),

  /** Sair da voz — desligar a chamada. */
  phoneOff: (
    <>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
      <path d="m2 2 20 20" />
    </>
  ),

  close: (
    <>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </>
  ),

  /** Editar. */
  pencil: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ),

  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),

  /** Mencionar. */
  at: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
    </>
  ),

  /** Expulsar do servidor. */
  userX: (
    <>
      <path d="M14 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="8" cy="7" r="4" />
      <path d="m17 8 5 5" />
      <path d="m22 8-5 5" />
    </>
  ),

  /** Banir. */
  ban: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </>
  ),

  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.9" />
      <path d="M16 3.1a4 4 0 0 1 0 7.8" />
    </>
  ),

  /** Criar convite: trazer alguém para dentro. */
  userPlus: (
    <>
      <path d="M15 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M19 8v6" />
      <path d="M22 11h-6" />
    </>
  ),

  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),

  /** Entrar com um convite. */
  login: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="m10 17 5-5-5-5" />
      <path d="M15 12H3" />
    </>
  ),

  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),

  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <path d="M9 9.5h.01" />
      <path d="M15 9.5h.01" />
    </>
  ),

  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </>
  ),

  /** Pôr um vídeo em foco. */
  maximize: (
    <>
      <path d="M15 3h6v6" />
      <path d="M9 21H3v-6" />
      <path d="M21 3l-7 7" />
      <path d="M3 21l7-7" />
    </>
  ),

  /** Voltar à grelha de vídeos. */
  minimize: (
    <>
      <path d="M4 14h6v6" />
      <path d="M20 10h-6V4" />
      <path d="M14 10l7-7" />
      <path d="M3 21l7-7" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

/**
 * O nome acessível vem de quem o usa — o `title` do botão, ou o texto ao
 * lado. O ícone é decoração em cima disso, logo `aria-hidden`: anunciá-lo
 * duas vezes seria pior que não o anunciar.
 */
export function Icon({ name, className }: { name: IconName; className?: string }): ReactElement {
  return (
    <svg
      className={className ? `ico ${className}` : 'ico'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
